package com.bikompanion.domain

import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sqrt

private const val EarthRadiusKm = 6371.0

/**
 * Straight-line distance in kilometres, equirectangular.
 *
 * Degrees of longitude shrink towards the poles, so comparing raw degree deltas overstates
 * east–west distance everywhere but the equator — at Barcelona's latitude by about a third.
 * Scaling `dLon` by `cos(latitude)` is enough to fix that, and over the tens of kilometres this
 * is used for the error against a proper haversine is far below the precision anyone needs.
 *
 * Distinct on purpose from the UI's `BikeNetwork.distanceSquaredTo`, which stays in raw degree
 * space because it only ever *orders* networks. This one is compared against a real kilometre
 * threshold, so it has to carry real units.
 */
fun distanceKmBetween(
    fromLatitude: Double,
    fromLongitude: Double,
    toLatitude: Double,
    toLongitude: Double,
): Double {
    val meanLatitudeRadians = (fromLatitude + toLatitude) / 2 * PI / 180
    val deltaLatitude = (toLatitude - fromLatitude) * PI / 180
    val deltaLongitude = (toLongitude - fromLongitude) * PI / 180 * cos(meanLatitudeRadians)
    return EarthRadiusKm * sqrt(deltaLatitude * deltaLatitude + deltaLongitude * deltaLongitude)
}
